import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import {
	RoleEnumKey,
	userIndexQueryKey,
	userListInvitationsQueryKey,
	useUserAssignRole,
	useUserIndex,
	useUserInvite,
	useUserListInvitations,
	useUserRemoveMember,
	useUserRevokeInvitation,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useIsJobillan } from '../../../../../hooks'
import { useAlert } from '../../../../rapid-ui'

export function useOrganizationViewModel() {
	const alert = useAlert()
	const queryclient = useQueryClient()
	const isJobillan = useIsJobillan()

	const [currentPage, setCurrentPage] = useState(1)
	const [itemsPerPage, setItemsPerPage] = useState(10)
	const [showInviteDialog, setShowInviteDialog] = useState(false)
	const [inviteEmails, setInviteEmails] = useState('')
	const [inviteRole, setInviteRole] = useState<RoleEnumKey>('member')
	const [invitationErrors, setInvitationErrors] = useState<Record<string, string>>({})

	const [invitationsPage, setInvitationsPage] = useState(1)
	const [invitationsPerPage, setInvitationsPerPage] = useState(10)
	const [showRevokeDialog, setShowRevokeDialog] = useState(false)
	const [selectedInvitation, setSelectedInvitation] = useState<
		{
			id: string
			email: string
		} | null
	>(null)

	const [showRemoveMemberDialog, setShowRemoveMemberDialog] = useState(false)
	const [selectedMember, setSelectedMember] = useState<
		{
			userId: string
			name: string
			email: string
		} | null
	>(null)

	const offset = (currentPage - 1) * itemsPerPage
	const invitationsOffset = (invitationsPage - 1) * invitationsPerPage

	const { user } = useAuth()
	const organizationId = useJobillaOrgId()

	const {
		data: users,
		status,
		isSuccess,
		isPlaceholderData,
	} = useUserIndex({
		'filter[include_jobillans]': true,
		'limit[number]': itemsPerPage,
		'offset[number]': offset,
	}, {
		placeholderData: (previousData: any) => previousData,
		staleTime: 5000,
	})

	const {
		data: invitations,
		isLoading: isLoadingInvitations,
		isPlaceholderData: isPlaceholderInvitations,
		isSuccess: isSuccessInvitations,
	} = useUserListInvitations({
		'filter[limit][number]': invitationsPerPage,
		'filter[offset][number]': invitationsOffset,
	}, {
		placeholderData: (previousData: any) => previousData,
		staleTime: 5000,
	})

	const inviteMutation = useUserInvite({
		mutationKey: ['invite-users'],
		onSuccess: (response) => {
			void queryclient.invalidateQueries({
				queryKey: userIndexQueryKey({
					'filter[include_jobillans]': true,
					'limit[number]': itemsPerPage,
					'offset[number]': offset,
				}),
			})
			void queryclient.invalidateQueries({
				queryKey: userListInvitationsQueryKey({
					'filter[limit][number]': invitationsPerPage,
					'filter[offset][number]': invitationsOffset,
				}),
			})

			// Check if any invitations failed
			const failedCount = Number(response?.data?.summary?.failed ?? 0)
			if (failedCount > 0) {
				// Parse errors from failed invitations
				const errors: Record<string, string> = {}
				const results = response?.data?.results
				if (Array.isArray(results)) {
					results.forEach((result: any) => {
						if (!result.success && result.error) {
							try {
								const errorData = JSON.parse(result.error)

								errors[result.email] = errorData?.errors?.[0]?.long_message
									|| errorData?.errors?.[0]?.message || 'Invalid email address'
							} catch {
								errors[result.email] = 'Invalid email address'
							}
						}
					})
				}
				setInvitationErrors(errors)

				// Show alert for partial success if some succeeded
				const successfulCount = Number(response?.data?.summary?.successful ?? 0)
				if (successfulCount > 0) {
					alert.open(
						'Partial Success',
						`${successfulCount} invitation(s) sent successfully. ${failedCount} invitation(s) failed. Please correct the errors and try again.`,
						'OK',
						() => {},
					)
				}
			} else {
				// All succeeded, close dialog and reset
				setShowInviteDialog(false)
				setInviteEmails('')
				setInviteRole('member')
				setInvitationErrors({})
			}
		},
		onError: (error) => {
			// Handle network or server errors
			alert.open(
				'Error',
				'Failed to send invitations. Please try again.',
				'OK',
				() => {},
			)
		},
	})

	const changeRole = useUserAssignRole({
		mutationKey: ['change-user-role'],
		onMutate: async (variables) => {
			const queryKey = userIndexQueryKey({
				'filter[include_jobillans]': true,
				'limit[number]': itemsPerPage,
				'offset[number]': offset,
			})

			await queryclient.cancelQueries({
				queryKey,
			})
			const previousUsers = queryclient.getQueryData(queryKey)
			void queryclient.setQueryData(
				queryKey,
				(oldData: any) => {
					if (!oldData) {
						return oldData
					}
					const newData = { ...oldData }
					newData.data = newData.data.map((user: any) => {
						if (user.user_id === variables.data?.userId) {
							return {
								...user,
								role: variables.data?.role,
							}
						}
						return user
					})
					return newData
				},
			)
			return { previousUsers }
		},
		onError: (_error) => {
			alert.open('Error', 'Error changing the user role', 'OK', () => {
				void queryclient.invalidateQueries({
					queryKey: userIndexQueryKey({
						'filter[include_jobillans]': true,
						'limit[number]': itemsPerPage,
						'offset[number]': offset,
					}),
				})
			})
		},
		onSuccess: () => {
			void queryclient.invalidateQueries({
				queryKey: userIndexQueryKey({
					'filter[include_jobillans]': true,
					'limit[number]': itemsPerPage,
					'offset[number]': offset,
				}),
			})
		},
	})

	const handleSendInvites = () => {
		if (!organizationId) {
			return
		}

		const emails = inviteEmails
			.split(/[\s,]+/)
			.map(email => email.trim())
			.filter(email => email.length > 0)

		void inviteMutation.mutateAsync({
			data: {
				organisationId: organizationId,
				role: inviteRole,
				invitations: emails.map(email => ({ email })),
				inviterUserId: user?.id ?? null,
			},
		})
	}

	const handleCancelInvite = () => {
		setShowInviteDialog(false)
		setInviteEmails('')
		setInviteRole('member')
		setInvitationErrors({})
	}

	const handleRoleChange = (newRole: RoleEnumKey, userId: string) => {
		changeRole.mutate({
			data: {
				userId: userId,
				role: newRole,
			},
		})
	}

	const removeMemberMutation = useUserRemoveMember({
		mutationKey: ['organisation-remove-member'],
		onError: () => {
			alert.open('Error', 'Failed to remove the member. Please try again.', 'OK', () => {})
		},
		onSuccess: () => {
			setShowRemoveMemberDialog(false)
			setSelectedMember(null)
			void queryclient.invalidateQueries({
				queryKey: userIndexQueryKey(),
			})
		},
	})

	const handleRemoveMemberClick = (userId: string, name: string, email: string) => {
		setSelectedMember({ userId, name, email })
		setShowRemoveMemberDialog(true)
	}

	const handleConfirmRemoveMember = () => {
		if (selectedMember) {
			removeMemberMutation.mutate({ user: selectedMember.userId })
		}
	}

	const handleCancelRemoveMember = () => {
		if (removeMemberMutation.isPending) {
			return
		}
		setShowRemoveMemberDialog(false)
		setSelectedMember(null)
	}

	const revokeInvitationMutation = useUserRevokeInvitation({
		mutationKey: ['revoke-invitation'],
		onError: () => {
			alert.open('Error', 'Failed to revoke invitation. Please try again.', 'OK', () => {})
			void queryclient.invalidateQueries({
				queryKey: userListInvitationsQueryKey({
					'filter[limit][number]': invitationsPerPage,
					'filter[offset][number]': invitationsOffset,
				}),
			})
		},
		onSuccess: () => {
			setShowRevokeDialog(false)
			setSelectedInvitation(null)
			void queryclient.invalidateQueries({
				queryKey: userListInvitationsQueryKey({
					'filter[limit][number]': invitationsPerPage,
					'filter[offset][number]': invitationsOffset,
				}),
			})
		},
	})

	const totalUsers = users?.meta?.total ?? 0
	const totalPages = totalUsers > 0 ? Math.ceil(totalUsers / itemsPerPage) : 1
	const actualItemsOnPage = users?.data?.length ?? 0

	const handlePreviousPage = () => {
		if (currentPage > 1) {
			setCurrentPage(currentPage - 1)
		}
	}

	const handleNextPage = () => {
		if (currentPage < totalPages) {
			setCurrentPage(currentPage + 1)
		}
	}

	const handleItemsPerPageChange = (value: string) => {
		const newItemsPerPage = parseInt(value, 10)
		setItemsPerPage(newItemsPerPage)
		setCurrentPage(1)
	}

	const handleRevokeClick = (invitationId: string, email: string) => {
		setSelectedInvitation({ id: invitationId, email })
		setShowRevokeDialog(true)
	}

	const handleConfirmRevoke = () => {
		if (selectedInvitation) {
			revokeInvitationMutation.mutate({ data: { invitationId: selectedInvitation.id } })
		}
	}

	const handleCancelRevoke = () => {
		setShowRevokeDialog(false)
		setSelectedInvitation(null)
	}

	const totalInvitations = invitations?.meta?.total ?? 0
	const totalInvitationsPages = totalInvitations > 0 ? Math.ceil(totalInvitations / invitationsPerPage) : 1

	const handleInvitationsPreviousPage = () => {
		if (invitationsPage > 1) {
			setInvitationsPage(invitationsPage - 1)
		}
	}

	const handleInvitationsNextPage = () => {
		if (invitationsPage < totalInvitationsPages) {
			setInvitationsPage(invitationsPage + 1)
		}
	}

	const handleInvitationsPerPageChange = (value: string) => {
		const newItemsPerPage = parseInt(value, 10)
		setInvitationsPerPage(newItemsPerPage)
		setInvitationsPage(1)
	}

	return {
		users: users?.data,
		currentUserId: user?.id ?? null,
		isSuccess,
		status,
		isPlaceholderData,
		totalUsers,
		totalPages,
		actualItemsOnPage,
		isJobillan,
		currentPage,
		itemsPerPage,
		showInviteDialog,
		inviteEmails,
		inviteRole,
		invitationErrors,
		setShowInviteDialog,
		setInviteEmails,
		setInviteRole,
		handleSendInvites,
		handleCancelInvite,
		handleRoleChange,
		showRemoveMemberDialog,
		selectedMember,
		handleRemoveMemberClick,
		handleConfirmRemoveMember,
		handleCancelRemoveMember,
		isRemovingMember: removeMemberMutation.isPending,
		handlePreviousPage,
		handleNextPage,
		handleItemsPerPageChange,
		invitations: (invitations)?.data,
		isLoadingInvitations,
		isPlaceholderInvitations,
		isSuccessInvitations,
		totalInvitations,
		invitationsPage,
		invitationsPerPage,
		showRevokeDialog,
		selectedInvitation,
		handleRevokeClick,
		handleConfirmRevoke,
		handleCancelRevoke,
		handleInvitationsPreviousPage,
		handleInvitationsNextPage,
		handleInvitationsPerPageChange,
		isRevokingInvitation: revokeInvitationMutation.isPending,
		setInvitationsPage,
	}
}
